import axios, { AxiosError } from 'axios';
import type { User } from '../types';

// ── Refresh-token concurrency lock ────────────────────────
// Prevents multiple 401 handlers firing parallel refresh requests.
let refreshPromise: Promise<User> | null = null;

/* eslint-disable @typescript-eslint/no-unused-vars */
export async function signIn(userName: string, password: string) {
    let data: User = { isOk: false };
    await axios.post('/api/Authentication/Authenticate', { userName, password }).then(res => {
      if (res.status === 200) {
          data = JSON.parse(JSON.stringify(res.data));
          localStorage.setItem('user', JSON.stringify(res.data));
        }
        else {
          localStorage.removeItem('user');
        }
    }).catch(error => {
        data = {
            isOk: false,
            message: error.response?.data ?? 'Authentication failed'
        };
    });

    return data;
}

export async function refresh(): Promise<User> {
    // If a refresh is already in flight, piggyback on it
    if (refreshPromise) return refreshPromise;

    refreshPromise = _doRefresh();
    try {
        return await refreshPromise;
    } finally {
        refreshPromise = null;
    }
}

async function _doRefresh(): Promise<User> {
    const existing: User | null = JSON.parse(localStorage.getItem('user') ?? 'null');
    if (!existing?.token || !existing?.refreshToken) {
        return { isOk: false, message: 'No session' };
    }

    try {
        const res = await axios.post('/api/Authentication/Refresh', {
            accessToken: existing.token,
            refreshToken: existing.refreshToken,
        });

        if (res.status === 200) {
            // Merge: keep any fields from the existing session that the
            // server didn't return (safety-net for backward compat).
            const merged: User = { ...existing, ...res.data };
            localStorage.setItem('user', JSON.stringify(merged));
            localStorage.setItem('token', merged.token!);
            return merged;
        }

        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return { isOk: false, message: 'Refresh failed' };
    } catch (e) {
        const error = e as AxiosError;
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return { isOk: false, message: error.message };
    }
}

export async function getUser() {
    let data: User = { isOk: false };
    try {
        // Send request
        data = JSON.parse(localStorage.getItem('user')!);
        return data;
    }
    catch {
        return data;
    }
}

export async function createAccount(email: string, password: string) {
  try {
    // Send request
    return {
      isOk: true,
    };
  } catch {
    return {
      isOk: false,
      message: 'Failed to create account',
    };
  }
}

export async function changePassword(password: string, email: string) {
  try {
    const userData: User = JSON.parse(localStorage.getItem('user')!);
    const res = await axios.post('/api/Authentication/ChangePassword', { password: password, email: email }, getHeaders(userData.token));
    if (res.status === 200) {
      return {
        isOk: true,
      };
    } else {
      return {
        isOk: false,
        message: res.statusText, // Return the status text from the response
      };
    }
  } catch (error: any) {
    const errorMessage = error.response?.data || 'Failed to change password'; // You can customize this based on the structure of the error response
    return {
      isOk: false,
      message: errorMessage,
    };
  }
}

export async function resetPassword(email: string) {
  try {
    // Send request
    return {
      isOk: true,
    };
  } catch {
    return {
      isOk: false,
      message: 'Failed to reset password',
    };
  }
}

const getHeaders = (token) => {
  return {
      headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
      }
  };
};
